/** Live data indicator with auto-refresh toggle and manual refresh button. */
import React from "react";
import {
  Box,
  Switch,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  keyframes,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

/** Props for LiveIndicator component. */
export interface LiveIndicatorProps {
  autoRefresh: boolean;
  onToggleRefresh: () => void;
  onManualRefresh: () => void;
  refreshing: boolean;
  interval?: number;
}

/** Pulsing animation for the live indicator dot. */
const pulseAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  100% {
    opacity: 0.5;
    transform: scale(1);
  }
`;

/** Live data indicator component. */
export default function LiveIndicator({
  autoRefresh,
  onToggleRefresh,
  onManualRefresh,
  refreshing,
  interval = 30,
}: LiveIndicatorProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: "background.paper",
        borderRadius: 1,
      }}
    >
      {/* Live indicator dot with pulsing animation */}
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: "success.main",
          animation: `${pulseAnimation} 2s ease-in-out infinite`,
        }}
      />

      {/* Live text label */}
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Live
      </Typography>

      {/* Auto-refresh toggle switch */}
      <Tooltip title={`Auto-refresh every ${interval} seconds`} arrow>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Switch
            checked={autoRefresh}
            onChange={onToggleRefresh}
            size="small"
            color="primary"
          />
          <Typography variant="caption" sx={{ minWidth: 30 }}>
            {interval}s
          </Typography>
        </Box>
      </Tooltip>

      {/* Manual refresh button */}
      <Tooltip title="Refresh now" arrow>
        <IconButton
          onClick={onManualRefresh}
          disabled={refreshing}
          size="small"
          sx={{
            bgcolor: refreshing ? "action.disabledBackground" : "action.hover",
            "&:hover": {
              bgcolor: refreshing ? "action.disabledBackground" : "action.selected",
            },
          }}
         aria-label="Refresh">
          {refreshing ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
